import { Link, useHistory } from 'react-router-dom';
import { useUpdateAtom } from 'jotai/utils';
import { Button, Footer } from 'src/components';
import { useOnboardingContext } from '../context';
import StepBar from '../components/StepBar';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { useAiAgentAvailable } from 'src/hooks/useAiAgentAvailable';
import { chatOpenAtom } from 'src/shared/chat';
import { config } from 'src/utils/config';
import styles from '../styles.module.scss';

type GuideItem = {
  title: string;
  description: string;
  icon: string;
  href: string;
  external?: boolean;
  onClick?: () => void;
};

const ADMIN_EXPLORE_ITEMS: GuideItem[] = [
  {
    title: 'Your Dashboard',
    description: 'Usage analytics and account overview.',
    icon: '📊',
    href: '/usage',
  },
  {
    title: 'Settings & Configuration',
    description: 'Security, branding, storage, webhooks, and more.',
    icon: '⚙️',
    href: '/admin/settings/',
  },
  {
    title: 'Manage Schemas',
    description: 'View and customise the metadata schemas you selected.',
    icon: '🗂️',
    href: '/admin/schemas',
  },
];

const NON_ADMIN_EXPLORE_ITEMS: GuideItem[] = [
  {
    title: 'Your Dashboard',
    description: 'Browse and manage your assets.',
    icon: '📊',
    href: '/assets',
  },
  {
    title: 'Collections',
    description: 'Organise assets into collections.',
    icon: '📁',
    href: '/collections',
  },
  {
    title: 'Shares',
    description: 'Share assets with others.',
    icon: '🔗',
    href: '/shares',
  },
];

const DEVELOPER_ITEMS: GuideItem[] = [
  {
    title: 'API Documentation',
    description: 'Build custom integrations with the FileSpin REST API.',
    icon: '📄',
    href: config.apiDocsUrl || 'https://developers.filespin.io',
    external: true,
  },
  {
    title: 'AI Agent Integration',
    description: 'MCP server setup, OAuth, and agentic workflow reference.',
    icon: '🤖',
    href: 'https://developers.filespin.io/guides/ai-agent-integration',
    external: true,
  },
  {
    title: 'Asset Lifecycle Guide',
    description: 'Upload, process, and deliver: the complete workflow.',
    icon: '🔄',
    href: 'https://developers.filespin.io/guides/asset-lifecycle',
    external: true,
  },
  {
    title: 'Metadata Schemas Guide',
    description: 'Organising assets with custom metadata schemas.',
    icon: '🏷️',
    href: 'https://developers.filespin.io/guides/asset-schemas',
    external: true,
  },
  {
    title: 'Branded Share Pages',
    description: 'Distribution, review, and delivery.',
    icon: '🔗',
    href: 'https://developers.filespin.io/guides/sharing-assets',
    external: true,
  },
];

const GuideCard = ({ item }: { item: GuideItem }) => {
  if (item.external) {
    return (
      <a
        key={item.title}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.guideCard}
      >
        <div className={styles.guideCardIcon}>{item.icon}</div>
        <div className={styles.guideCardContent}>
          <h4>{item.title}</h4>
          <p>{item.description}</p>
        </div>
        <div className={styles.guideCardArrow}>↗</div>
      </a>
    );
  }

  const card = (
    <>
      <div className={styles.guideCardIcon}>{item.icon}</div>
      <div className={styles.guideCardContent}>
        <h4>{item.title}</h4>
        <p>{item.description}</p>
      </div>
      <div className={styles.guideCardArrow}>→</div>
    </>
  );

  if (item.onClick) {
    return (
      <Link
        key={item.title}
        to={item.href}
        className={styles.guideCard}
        onClick={item.onClick}
      >
        {card}
      </Link>
    );
  }

  return (
    <Link key={item.title} to={item.href} className={styles.guideCard}>
      {card}
    </Link>
  );
};

const JumpOffGuide = () => {
  const { goToPreviousStep, demoCompleted, demoUsedInAppAgent, isAdmin, role } = useOnboardingContext();
  const history = useHistory();
  const setChatOpen = useUpdateAtom(chatOpenAtom);

  const showDeveloperResources = role === 'ADMIN' || role === 'MANAGER';
  const isUser = role === 'USER';
  const showSchedules = role === 'ADMIN' || role === 'MANAGER';
  const { inAppAvailable } = useAiAgentAvailable(role);

  const baseExploreItems = isAdmin
    ? ADMIN_EXPLORE_ITEMS
    : NON_ADMIN_EXPLORE_ITEMS.filter((item) => !(isUser && item.href === '/shares'));

  const aiAgentItems: GuideItem[] = inAppAvailable
    ? [
        {
          title: 'Open AI Agent',
          description: demoUsedInAppAgent
            ? 'Pick up the chat and ask the agent to do more.'
            : 'Chat with the AI Agent to search, share, and organize assets.',
          icon: '🤖',
          href: '/',
          onClick: () => setChatOpen(true),
        },
        ...(showSchedules
          ? [
              {
                title: 'Scheduled agents',
                description: 'Have an agent run a prompt on a repeating schedule, unattended.',
                icon: '⏱️',
                href: '/ai/schedules',
              } as GuideItem,
            ]
          : []),
      ]
    : [];

  const exploreItems: GuideItem[] = [...aiAgentItems, ...baseExploreItems];

  const progressMutation = useUpdateOnboardingProgressMutation();

  const handleFinish = () => {
    progressMutation.mutate({ completed: true });
    history.push('/');
  };

  return (
    <main className={styles.step}>
      <StepBar
        onGoToNextStep={handleFinish}
        onGoToPreviousStep={goToPreviousStep}
        hasUnsavedChanges
        nextLabel="Go to Dashboard"
      />
      <section className={styles.container}>
        <p className={styles.stepInfo}>
          You're all set! Here are some resources to help you get the most out of FileSpin.
        </p>

        {demoCompleted && (
          <div className={styles.demoBanner}>
            <span>✓</span>
            <span>
              {demoUsedInAppAgent
                ? 'You ran your first AI Agent task!'
                : 'You just ran your first AI workflow!'}
            </span>
          </div>
        )}

        <div className={styles.guideSection}>
          <h3 className={styles.guideSectionTitle}>Explore Your Workspace</h3>
          <div className={styles.guideGrid}>
            {exploreItems.map((item) => (
              <GuideCard key={item.title} item={item} />
            ))}
          </div>
        </div>

        {showDeveloperResources && (
          <div className={styles.guideSection}>
            <h3 className={styles.guideSectionTitle}>Developer Resources</h3>
            <div className={styles.guideGrid}>
              {DEVELOPER_ITEMS.map((item) => (
                <GuideCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        )}

        <p className={styles.guideHandoff}>
          {demoUsedInAppAgent
            ? 'Your conversation has been saved. Open the AI Agent any time from the toolbar to continue, or set up a schedule to run agent tasks automatically.'
            : demoCompleted
              ? 'Your sample assets are ready in the asset library. The Getting Started checklist on your dashboard will guide you through the remaining setup.'
              : 'The Getting Started checklist on your dashboard will guide you through the next steps.'}
        </p>

        <div className={styles.guideFinishSection}>
          <Button onClick={handleFinish} loading={progressMutation.isLoading}>
            Go to Dashboard
          </Button>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default JumpOffGuide;
